import { Injectable } from '@angular/core'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { PatientDashboardReq } from '../../model/inko-backend.model'
import { DashboardZeitraeume, mitAktuellenZeitraeumen } from '../../util/dashboard-filter'

/** Der Filter samt der Marken mitlaufender Zeitraeume (`DashboardZeitraeume`). */
export type PatientDashboardFilter = PatientDashboardReq & DashboardZeitraeume

/* Model */

export interface PatientDashboardStateModel {
  version: number
  patientColumns: {
    /**
     * All available column keys (in order)
     */
    keys: string[]
    /**
     * Currently visible column keys
     */
    visible: string[]
  }
  patientFilter: PatientDashboardFilter
  /**
   * Schluessel der fest eingebauten Kacheln, die der Nutzer entfernt hat. Bewusst eine Liste der
   * ENTFERNTEN statt der sichtbaren: so erreichen spaeter ergaenzte Preset-Kacheln auch Nutzer,
   * die ihre Kacheln schon einmal angepasst haben.
   */
  patientDismissedPresets?: string[]
  patientKpiCards: {
    title: string
    patientFilter: PatientDashboardFilter
  }[]
}

// Patient

export const patientAvailableColumns: { label: string; key: string; defaultVisible?: boolean }[] = [
  { label: 'Datum', key: 'createdDate', defaultVisible: true },
  { label: 'Patientenname', key: 'patientenname', defaultVisible: true },
  { label: 'E-Mail', key: 'email' },
  { label: 'Adresse', key: 'adresse' },
  { label: 'Korrespondenzadresse', key: 'korrespondenzadresse' },
  { label: 'Lieferadresse', key: 'lieferadresse' },
  { label: 'Pflegegrad', key: 'pflegegrad' },
  { label: 'Betreuungsform', key: 'betreuungsform' },
  { label: 'Versichertennummer', key: 'versichertennummer' },
  { label: 'Geburtsdatum', key: 'dateOfBirth' },
  { label: 'Krankenkasse', key: 'krankenkasse', defaultVisible: true },
  { label: 'Versicherungsart', key: 'versicherungsart', defaultVisible: false },
  { label: 'Zuzahlungsbefreiung', key: 'zuzahlungsbefreiung', defaultVisible: false },
  { label: 'SEPA-Lastschriftmandat', key: 'sepaMandat', defaultVisible: false },
  { label: 'Herkunft', key: 'externerPartner', defaultVisible: true },
  { label: 'Versorgungen', key: 'produktgruppen', defaultVisible: true },
  // Laufende Bemusterungen erscheinen ohnehin als Chip in der Versorgungen-Spalte; die eigene
  // Spalte ist der Nachschlag fuer „seit wann laeuft das schon" und deshalb per Default aus.
  { label: 'Bemusterung', key: 'bemusterung', defaultVisible: false },
]
export const patientColumnNameMap = patientAvailableColumns.reduce(
  (acc, col) => {
    acc[col.key] = col.label
    return acc
  },
  {} as Record<string, string>,
)

/* Actions */

// Patient

/**
 * Update the PHM columns from the available columns
 */
export class DashboardStatePatientUpdateColumns {
  static readonly type = '[Dashboard] Update Patient Columns'
}

export class DashboardStatePatientMoveColumns {
  static readonly type = '[Dashboard] Move Patient Columns'
  constructor(
    public indexA: number,
    public indexB: number,
  ) {}
}

export class DashboardStatePatientSetColumnVisible {
  static readonly type = '[Dashboard] Visible Patient Column'
  constructor(public keys: string[]) {}
}

export class DashboardStatePatientReset {
  static readonly type = '[Dashboard] Reset Patient Columns'
}

export class DashboardStatePatientSetFilter {
  static readonly type = '[Dashboard] Set Patient Filter'
  constructor(public filter: Partial<PatientDashboardFilter>) {}
}
export class DashboardStatePatientResetFilter {
  static readonly type = '[Dashboard] Reset Patient Filter'
}

export class DashboardStatePatientAddKpiCard {
  static readonly type = '[Dashboard] Add Patient KPI Card'
  constructor(
    public title: string,
    public filter: PatientDashboardFilter,
  ) {}
}
export class DashboardStatePatientRemoveKpiCard {
  static readonly type = '[Dashboard] Remove Patient KPI Card'
  constructor(public index: number) {}
}

export class DashboardStatePatientDismissPresetKpiCard {
  static readonly type = '[Dashboard] Dismiss Patient Preset KPI Card'
  constructor(public key: string) {}
}
export class DashboardStatePatientRestorePresetKpiCards {
  static readonly type = '[Dashboard] Restore Patient Preset KPI Cards'
}

/* State */

const defaultState: PatientDashboardStateModel = {
  version: 2,

  patientColumns: {
    keys: patientAvailableColumns.map((c) => c.key),
    visible: patientAvailableColumns.filter((c) => c.defaultVisible).map((c) => c.key),
  },
  patientFilter: {
    pageSize: 30,
    sortField: 'CREATED_DATE',
    sortOrder: 'DESC',
  },
  patientDismissedPresets: [],
  patientKpiCards: [],
}

@State<PatientDashboardStateModel>({
  name: 'patientDashboard',
  defaults: defaultState,
})
@Injectable()
export class PatientDashboardState {
  // Patient

  @Selector()
  static patientAvailableColumns(state: PatientDashboardStateModel) {
    return state.patientColumns.keys
  }
  @Selector()
  static patientVisibleColumns(state: PatientDashboardStateModel) {
    return state.patientColumns.visible
  }
  @Selector()
  static patientFilterCounter(state: PatientDashboardStateModel) {
    const filter = state.patientFilter
    return (
      (filter.healthInsurances?.length ?? 0) +
      (filter.externalPartnerIds?.length ?? 0) +
      (filter.sepaMandateStatuses?.length ?? 0) +
      (filter.betreuungsformen?.length ?? 0) +
      (filter.pflegegrade?.length ?? 0) +
      (filter.zuzahlungsbefreiungen?.length ?? 0) +
      (filter.insuranceType ? 1 : 0) +
      (filter.hasAnyVersorgungen !== undefined ? 1 : 0) +
      (filter.hasOffeneBemusterung !== undefined ? 1 : 0) +
      (filter.fromCreatedDate || filter.toCreatedDate ? 1 : 0)
    )
  }
  @Selector()
  static patientFilter(state: PatientDashboardStateModel) {
    // Mitlaufende Zeitraeume („Letzter Monat“) auf heute ausrechnen — der persistierte Stand kann von gestern sein.
    return mitAktuellenZeitraeumen(state.patientFilter)
  }

  @Selector()
  static patientKpiCards(state: PatientDashboardStateModel) {
    return state.patientKpiCards ?? []
  }

  @Selector()
  static patientDismissedPresets(state: PatientDashboardStateModel): string[] {
    // Persistierter Altstand kennt das Feld nicht — ohne Fallback waere es undefined.
    return state.patientDismissedPresets ?? []
  }

  // Patient
  @Action(DashboardStatePatientUpdateColumns)
  setPatientColumns(ctx: StateContext<PatientDashboardStateModel>) {
    const { version } = ctx.getState()
    if (version != defaultState.version) {
      ctx.setState({
        ...defaultState,
        patientKpiCards: ctx.getState().patientKpiCards ?? defaultState.patientKpiCards,
      })
      return
    }

    const columns = ctx.getState().patientColumns.keys.slice()
    const allColumnKeys = patientAvailableColumns.map((c) => c.key)

    // Add any new columns that are not already in the state
    allColumnKeys.forEach((key) => {
      if (!columns.includes(key)) {
        columns.push(key)
      }
    })
    // remove any columns that are no longer available
    const filteredColumns = columns.filter((key) => allColumnKeys.includes(key))

    ctx.patchState({
      patientColumns: {
        ...ctx.getState().patientColumns,
        keys: filteredColumns,
      },
    })
  }

  @Action(DashboardStatePatientMoveColumns)
  movePatientColumns(ctx: StateContext<PatientDashboardStateModel>, action: DashboardStatePatientMoveColumns) {
    const columns = ctx.getState().patientColumns.keys.slice()
    const [movedColumn] = columns.splice(action.indexA, 1)
    columns.splice(action.indexB, 0, movedColumn)
    ctx.patchState({
      patientColumns: {
        ...ctx.getState().patientColumns,
        keys: columns,
      },
    })
  }

  @Action(DashboardStatePatientSetColumnVisible)
  setPatientColumnVisible(ctx: StateContext<PatientDashboardStateModel>, action: DashboardStatePatientSetColumnVisible) {
    ctx.patchState({
      patientColumns: {
        ...ctx.getState().patientColumns,
        visible: action.keys,
      },
    })
  }

  @Action(DashboardStatePatientReset)
  resetPatientColumns(ctx: StateContext<PatientDashboardStateModel>) {
    ctx.patchState({
      patientColumns: defaultState.patientColumns,
    })
  }

  @Action(DashboardStatePatientSetFilter)
  setPatientFilter(ctx: StateContext<PatientDashboardStateModel>, action: DashboardStatePatientSetFilter) {
    ctx.patchState({
      patientFilter: {
        ...ctx.getState().patientFilter,
        ...action.filter,
      },
    })
  }

  @Action(DashboardStatePatientResetFilter)
  resetPatientFilter(ctx: StateContext<PatientDashboardStateModel>) {
    ctx.patchState({
      patientFilter: defaultState.patientFilter,
    })
  }

  @Action(DashboardStatePatientAddKpiCard)
  addPatientKpiCard(ctx: StateContext<PatientDashboardStateModel>, action: DashboardStatePatientAddKpiCard) {
    ctx.patchState({
      patientKpiCards: [
        ...(ctx.getState().patientKpiCards ?? []),
        {
          title: action.title,
          patientFilter: action.filter,
        },
      ],
    })
  }
  @Action(DashboardStatePatientRemoveKpiCard)
  removePatientKpiCard(ctx: StateContext<PatientDashboardStateModel>, action: DashboardStatePatientRemoveKpiCard) {
    const currentCards = (ctx.getState().patientKpiCards ?? []).slice()
    currentCards.splice(action.index, 1)
    ctx.patchState({
      patientKpiCards: currentCards,
    })
  }
  @Action(DashboardStatePatientDismissPresetKpiCard)
  dismissPatientPresetKpiCard(ctx: StateContext<PatientDashboardStateModel>, action: DashboardStatePatientDismissPresetKpiCard) {
    const dismissed = ctx.getState().patientDismissedPresets ?? []
    if (dismissed.includes(action.key)) {
      return
    }
    ctx.patchState({ patientDismissedPresets: [...dismissed, action.key] })
  }

  @Action(DashboardStatePatientRestorePresetKpiCards)
  restorePatientPresetKpiCards(ctx: StateContext<PatientDashboardStateModel>) {
    // Nur die Presets kommen zurueck; selbst angelegte Kacheln bleiben unangetastet.
    ctx.patchState({ patientDismissedPresets: [] })
  }
}
